import { useEffect, useState } from 'react';
import moment from 'moment';

/*
 * React hook to store data in localStorage
 * Copied from: https://usehooks.com/useLocalStorage/
 * But modified for Typescript and with the todayOnly and sessionOnly options
 * 2022-09-30 chris.morison@webtoolsagri.com
 */

interface UseLocalStorageOptions {
  todayOnly?: boolean;
  sessionOnly?: boolean;
}

function useLocalStorage<T>(
  key: string,
  initialValue: T,
  { todayOnly, sessionOnly } = {} as UseLocalStorageOptions
) {
  const storage = sessionOnly ? window?.sessionStorage : window?.localStorage;
  const today = todayOnly && moment().format('YYYY-MM-DD');
  const eventName = `useLocalStorage.${key}.changed`;

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (!storage) {
      return initialValue;
    }
    try {
      if (today) {
        // clear the property if it wasn't set today
        const date = storage.getItem(key + '__date');
        if (date && date != today) {
          storage.removeItem(key);
          storage.removeItem(key + '__date');
        }
      }
      // Get from storage by key
      const item = storage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      // If error also return initialValue
      console.log(error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that persists the new value to localStorage.
  const setValue = (value: T) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue({ ...valueToStore });
      // Save to local storage
      if (storage) {
        storage.setItem(key, JSON.stringify(valueToStore));
        if (today) {
          storage.setItem(key + '__date', today);
        }
      }
      // if multiple components use the same storage, we need to send them a message with the new value
      // note that the native 'storage' event doesn't work on the same page, so use our own event
      window.dispatchEvent(
        new CustomEvent<T>(eventName, { detail: valueToStore })
      );
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.log(error);
    }
  };

  // listen for our own storage change events from other components
  useEffect(() => {
    const handler = (data: Event) => {
      const newValue = (data as CustomEvent<T>).detail;
      setStoredValue({ ...newValue });
    };
    window.addEventListener(eventName, handler);

    // remove the listener when the component is dismounted
    return () => window.removeEventListener(eventName, handler);
  }, [eventName, setStoredValue]);

  const reset = () => setValue(initialValue);

  return [storedValue, setValue, reset] as const;
}

export default useLocalStorage;
